import { useState, useEffect, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  sessionError: string | null;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    sessionError: null,
  });
  const isSigningOutRef = useRef(false);
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      let sessionError = null;
      if (error) {
        console.error('Session error:', error);
        if (error.message.includes('refresh_token_not_found') || error.message.includes('Invalid Refresh Token')) {
          sessionError = 'Your session has expired. Please sign in again.';
        } else {
          sessionError = 'Authentication error. Please sign in again.';
        }
      }

      sessionRef.current = session;
      setAuthState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
        sessionError,
      }));
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Update ref to track current session
      sessionRef.current = session;
      
      // Use functional update to access current state without dependency
      setAuthState(prev => {
        // Prevent infinite loop: if we're signing out and receiving SIGNED_OUT event,
        // check if state is already what we want before updating
        if (event === 'SIGNED_OUT') {
          const newUser = null;
          const newSession = null;
          
          // If we're in the process of signing out and state is already cleared, skip update
          if (isSigningOutRef.current && !prev.user && !prev.session) {
            isSigningOutRef.current = false; // Reset the flag
            return prev; // Already cleared, no need to update
          }
          
          // Reset the flag
          isSigningOutRef.current = false;
          
          // Check if state is already what we want
          if (prev.user === newUser && prev.session === newSession) {
            return prev; // No change needed
          }
          
          // Update state to signed out
          return {
            ...prev,
            session: newSession,
            user: newUser,
            loading: false,
            sessionError: prev.user ? 'You have been signed out. Please sign in again.' : null,
          };
        }

        // Handle other events
        let sessionError = null;
        if (event === 'TOKEN_REFRESHED' && !session) {
          sessionError = 'Your session has expired. Please sign in again.';
        }

        const newUser = session?.user ?? null;
        const newSession = session;
        
        // Only update if the state actually changed
        if (prev.user === newUser && prev.session === newSession && prev.sessionError === sessionError) {
          return prev; // No change, prevent unnecessary update
        }

        return {
          ...prev,
          session: newSession,
          user: newUser,
          loading: false,
          sessionError,
        };
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  const clearSessionError = useCallback(() => {
    setAuthState(prev => ({
      ...prev,
      sessionError: null,
    }));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    // Clear any previous session errors
    setAuthState(prev => ({
      ...prev,
      sessionError: null,
    }));

    // Check if Supabase is properly configured
    if (!isSupabaseConfigured()) {
      return {
        data: null,
        error: {
          message: 'Supabase is not configured. Please click "Connect to Supabase" in the top right or set up your .env file with valid Supabase credentials.'
        }
      };
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (fetchError) {
      return {
        data: null,
        error: {
          message: 'Unable to connect to Supabase. Please check your internet connection and Supabase configuration.'
        }
      };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured()) {
      return {
        data: null,
        error: {
          message: 'Supabase is not configured. Please set up your environment variables first.'
        }
      };
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { data, error };
  }, []);

  const signOut = useCallback(async () => {
    // Set flag to prevent infinite loop in onAuthStateChange
    isSigningOutRef.current = true;
    
    // Check if we have a session using the ref (avoids dependency on authState)
    const hadSession = sessionRef.current !== null;
    
    // Clear localStorage
    localStorage.removeItem('activeView');
    
    // Clear all sessionStorage items related to portal
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('portal_') || key.startsWith('client-portal_') || key.includes('portal_email') || key === 'rapidcert_workspace_override')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Always clear local state first, regardless of API call success
    // This ensures users can always "log out" from the UI perspective
    const clearLocalState = () => {
      isSigningOutRef.current = false;
      setAuthState(prev => ({
        ...prev,
        user: null,
        session: null,
      }));
      sessionRef.current = null;
      
      // Also clear Supabase's internal storage to ensure complete logout
      try {
        // Clear Supabase session from localStorage
        const supabaseKeys = Object.keys(localStorage).filter(key => 
          key.startsWith('sb-') || key.includes('supabase')
        );
        supabaseKeys.forEach(key => localStorage.removeItem(key));
      } catch (e) {
        // Ignore errors clearing storage
      }
    };

    // Only attempt to sign out if we had a session
    // This prevents unnecessary API calls and console errors when session is already invalid
    if (hadSession) {
      // Temporarily suppress console.error and console.warn for this specific call
      const originalConsoleError = console.error;
      const originalConsoleWarn = console.warn;
      
      try {
        // Suppress console messages for session_not_found errors
        console.error = (...args: any[]) => {
          const errorStr = JSON.stringify(args);
          if (errorStr.includes('session_not_found') || 
              errorStr.includes('Session from session_id claim in JWT does not exist') ||
              errorStr.includes('403') && errorStr.includes('logout')) {
            return; // Don't log this error
          }
          originalConsoleError.apply(console, args);
        };
        
        console.warn = (...args: any[]) => {
          const warnStr = JSON.stringify(args);
          if (warnStr.includes('session_not_found') || 
              warnStr.includes('Session from session_id claim in JWT does not exist')) {
            return; // Don't log this warning
          }
          originalConsoleWarn.apply(console, args);
        };
        
        const { error } = await supabase.auth.signOut({ scope: 'global' }).catch((err: any) => {
          // Catch network/fetch errors and check if they're session-related
          if (err?.status === 403 || 
              err?.message?.includes('session_not_found') || 
              err?.message?.includes('Session from session_id claim in JWT does not exist') ||
              (err?.response?.body && JSON.stringify(err.response.body).includes('session_not_found'))) {
            // Session was already invalid - clear local state and treat as success
            clearLocalState();
            return { error: null };
          }
          // For other errors, still clear state but return the error
          clearLocalState();
          return { error: err };
        });
        
        // Restore console methods
        console.error = originalConsoleError;
        console.warn = originalConsoleWarn;
        
        // Clear state regardless of error (session_not_found means we're already logged out)
        if (error && (
          error.message?.includes('session_not_found') || 
          error.message?.includes('Session from session_id claim in JWT does not exist') ||
          (error as any)?.status === 403
        )) {
          clearLocalState();
          return { error: null };
        }
        
        // If no error or non-session error, clear state anyway (onAuthStateChange might not fire)
        clearLocalState();
        return { error };
      } catch (err: any) {
        // Restore console methods in case of exception
        console.error = originalConsoleError;
        console.warn = originalConsoleWarn;
        
        // Always clear state, even on error
        clearLocalState();
        
        // Ignore 403 errors for session_not_found - session is already invalid
        if (err?.status === 403 || 
            err?.message?.includes('session_not_found') ||
            err?.message?.includes('Session from session_id claim in JWT does not exist') ||
            (err?.response?.body && JSON.stringify(err.response.body).includes('session_not_found'))) {
          return { error: null };
        }
        return { error: err };
      }
    } else {
      // No session in state - already logged out
      // Clear state manually since we won't get a SIGNED_OUT event
      clearLocalState();
      return { error: null };
    }
  }, []);

  return {
    ...authState,
    signIn,
    signUp,
    signOut,
    clearSessionError,
  };
}